import "./timeline-event.scss";

import {
  MediaAsset,
  MediaAssetProps,
} from "design-system/components/primitives/media-asset/media-asset";
import { EditAttributes } from "design-system/types/types";
import {
  convertToDate,
  reformatDateString,
} from "design-system/utils/date-format";
import { ReactNode } from "react";

export interface TimelineEventProps {
  content: {
    title?: ReactNode;
    dek?: ReactNode;
    media?: MediaAssetProps;
    editAttributes?: {
      title?: EditAttributes;
      dek?: EditAttributes;
    };
  };
  dates: Array<DateItem>;
  dateFormat?: string;
  endDatePresent?: boolean;
  HeadingLevelStart?: "h2" | "h3";
}

interface DateItem {
  date: string;
  editAttributes?: {
    date?: EditAttributes;
  };
}

/**
 * ## See it in use on...
 * - A [detail page with timeline content](/story/example-pages-detail-pages-timeline--story)
 *
 * - **`id: CB-021-000-00`**
 * - **`data-region: cb__timeline-event`**
 */
export function TimelineEvent({
  dates,
  dateFormat = "Full Date",
  content,
  endDatePresent = false,
  HeadingLevelStart = "h3",
}: TimelineEventProps) {
  const HeadlingLevelTitle = HeadingLevelStart === "h2" ? "h3" : "h4";

  // Choose tag dynamically — heading when content exists, div otherwise for correct spacing
  const DateTag =
    (dates && dates.length > 0) || endDatePresent ? HeadingLevelStart : "div";

  return (
    <div
      className="hbs-timeline-event-wrapper"
      data-region="cb__timeline-event"
    >
      <div className="hbs-timeline-event">
        <DateTag className="hbs-timeline-event__date">
          {(dates ?? []).map((dateItem, i) => {
            const date = convertToDate(reformatDateString(dateItem.date));

            if (!date) return null;

            if (i === 1 && endDatePresent) return null;

            const year = date.getFullYear();
            const monthName = new Intl.DateTimeFormat("en-US", {
              month: "long",
            }).format(date);
            const day = date.getDate();

            return (
              <div
                {...dateItem?.editAttributes?.date}
                className="hbs-timeline-event__single-date"
                key={i}
              >
                {i === 1 && (
                  <span className="hbs-global-visually-hidden"> to </span>
                )}
                {dateFormat !== "Year Only" && (
                  <span className="hbs-timeline-event__date-day-month">
                    {monthName} {dateFormat === "Full Date" ? day + "," : ""}
                  </span>
                )}
                <span className="hbs-timeline-event__date-year">{year}</span>
              </div>
            );
          })}
          {endDatePresent && (
            <div className="hbs-timeline-event__single-date">
              <span className="hbs-timeline-event__date-year--present">
                <span className="hbs-global-visually-hidden"> to </span> Present
              </span>
            </div>
          )}
        </DateTag>
        <div className="hbs-timeline-event__content">
          <div className="hbs-timeline-event__media">
            <MediaAsset {...content.media} aspectRatio="3/2" />
          </div>
          <div className="hbs-timeline-event__text">
            {content.title && (
              <HeadlingLevelTitle
                {...content.editAttributes?.title}
                className="hbs-timeline-event__title"
              >
                {content.title}
              </HeadlingLevelTitle>
            )}
            <div
              {...content.editAttributes?.dek}
              className="hbs-timeline-event__dek"
            >
              {content.dek}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
